import { Dialog } from "@mui/material";
import * as React from "react";

import { AlertDialog, PageLayout } from "@/shared/components";
import { AddToCollection } from "@/shared/components/collections";
import { EventsList } from "@/shared/components/events";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type SelectionOption } from "@/shared/components/ResourceView";
import { EventRecord } from "@/shared/domain/events/EventRecord";
import { useDeleteEvents } from "@/shared/domain/events/hooks";
import {
  DeleteOutlineIcon,
  PlaylistAddIcon,
} from "@/shared/foundation/icons/mui";

/** Rows a bulk action targets, plus the callback that clears the selection on completion. */
interface PendingEventAction {
  ids: string[];
  clearSelection: () => void;
}

export function EventsPage() {
  const { mutateAsync: deleteEvents } = useDeleteEvents();
  const showNotification = useNotification();

  const [pendingAdd, setPendingAdd] = React.useState<PendingEventAction | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] =
    React.useState<PendingEventAction | null>(null);

  const selectionOptions: SelectionOption<EventRecord>[] = React.useMemo(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          setPendingAdd({
            ids: selected.map((e) => e.event_id),
            clearSelection,
          });
        },
      },
      {
        name: "Delete",
        icon: <DeleteOutlineIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          setPendingDelete({
            ids: selected.map((e) => e.event_id),
            clearSelection,
          });
        },
      },
    ],
    [],
  );

  const handleDeleteConfirm = async () => {
    if (pendingDelete === null) {
      return;
    }
    try {
      await deleteEvents(pendingDelete.ids);
      pendingDelete.clearSelection();
    } catch {
      showNotification(
        `${pendingDelete.ids.length} event${pendingDelete.ids.length !== 1 ? "s" : ""} could not be deleted`,
        "error",
      );
    }
  };

  return (
    <PageLayout title="Roboto - Events" headerContentLeft="Events">
      <EventsList scope={{ type: "org" }} selectionOptions={selectionOptions} />
      <Dialog open={pendingAdd !== null} onClose={() => setPendingAdd(null)}>
        <AddToCollection
          selected={{ type: "Event", items: new Set(pendingAdd?.ids ?? []) }}
          onCancel={() => setPendingAdd(null)}
          onSuccess={() => pendingAdd?.clearSelection()}
        />
      </Dialog>
      <AlertDialog
        dialogOpen={pendingDelete !== null}
        handleClose={() => setPendingDelete(null)}
        dialogTitle={`Delete ${pendingDelete?.ids.length ?? 0} event${pendingDelete?.ids.length !== 1 ? "s" : ""}?`}
        dialogText="This action cannot be undone."
        dialogAction={handleDeleteConfirm}
      />
    </PageLayout>
  );
}
